import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import AnnouncementIcon from '@mui/icons-material/Announcement'
import { useAuthContext } from 'AuthProvider'
import { type CellValueChangedEvent, type FindChangedEvent } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { LoadingOverlay, EditStatusCellRenderer } from 'components/commonparts/index'
import { useGridEdit} from 'hooks/useGridEdit'
import { useItem } from 'hooks/useItems';
import { initRecordEditEx, type DataListPropsBase, type RecordEditEx } from 'types/dataGridTypes'
import { convertDateToRequestParam } from 'utils/convertFromLocalDate'
import { validate4to13Length, validate4to14Length, validate6to10Length, validateRequired, validateWeight } from 'utils/validation'

import type { ColDef, ColGroupDef, ValueFormatterParams, ValueParserParams, IRichCellEditorParams } from 'ag-grid-enterprise'
import type { Item, MstItem, MstSupplier, Groups, GetMstItemsItemGetRequest, Codes } from 'api-client'
import type { ItemAlertDialogEdit } from 'components/dialog/ItemAlertDialog/ItemAlertDialog'
import 'utils/mst.scss'

const columnMapping: Record<string, string> = {
    "item_code": "itemCode",
    "org_item_code": "orgItemCode",
    "item_name": "itemName",
    "sales_startdate": "salesStartdate",
    "sales_enddate": "salesEnddate",
    "longside": "longside",
    "shortside": "shortside",
    "height": "height",
    "weight": "weight",
    "spec": "spec",
    "max_pallet_quantity": "maxPalletQuantity",
    "pallet_quantity_face": "palletQuantityFace",
    "pallet_quantity_tier": "palletQuantityTier",
    "jan": "jan",
    "jan_case": "janCase",
    "gtin_13": "gtin13",
    "gtin_14": "gtin14",
    "itf": "itf",
    "sdp": "sdp",
    "replace_org_item_code": "replaceOrgItemCode",
    "maker_gln": "makerGln",
    "maker_name": "makerName",
    "org_supplier_id": "orgSupplierId",
    "order_unit_type": "orderUnitType",
    "case_lot": "caseLot",
    "min_order_quantity": "minOrderQuantity",
    "is_muted": "isMuted",
    "is_discontinued": "isDiscontinued",
    remarks: 'remarks',
    "delete_flg": "deleteFlg"
};

const primayKey = ['orgItemCode']

interface DataListProps extends DataListPropsBase {
  list: Item
  searchParam: GetMstItemsItemGetRequest
  supplier: MstSupplier[];
  systype: Groups[];
  findSearchValue?: string;
  onFindChanged?: (event: FindChangedEvent) => void;
  gridRef?: React.RefObject<AgGridReact>;
  isSearchVisible?: boolean;
  dialogEdits?: ItemAlertDialogEdit[]
  focusTarget?: { orgItemCode: string }
  onFocusHandled?: () => void
}

interface MstItemWithId extends MstItem, RecordEditEx {}

const trimPk = (value: string | null | undefined) => String(value ?? '').trim()

function syncDialogEditsToAgGrid(
  gridRef: React.RefObject<AgGridReact | null | undefined>,
  adds: MstItemWithId[],
  updates: MstItemWithId[]
) {
  if (adds.length === 0 && updates.length === 0) return
  requestAnimationFrame(() => {
    const gridApi = gridRef.current?.api
    if (!gridApi || gridApi.isDestroyed()) return
    gridApi.applyTransaction({ add: adds, update: updates })
    gridApi.refreshCells({ force: true })
  })
}

function applyItemDialogEdits(
  prev: MstItemWithId[],
  dialogEdits: ItemAlertDialogEdit[],
  createBlankRow: () => MstItemWithId
): { next: MstItemWithId[]; adds: MstItemWithId[]; updates: MstItemWithId[] } {
  const next = [...prev]
  const adds: MstItemWithId[] = []
  const updates: MstItemWithId[] = []

  dialogEdits.forEach((edit) => {
    const editItem = trimPk(edit.orgItemCode)
    if (!editItem) return

    let idx = next.findIndex((row) => trimPk(row.orgItemCode) === editItem)

    let needsBlankRow = false
    let target: MstItemWithId | undefined

    if (idx >= 0) {
      target = next[idx]
      if (edit.masterRegister && !target.isNewRecord && target.insertDatetime != null) {
        idx = -1
        target = undefined
      }
    }

    if (idx < 0 && (edit.masterRegister || edit.isDiscontinued || edit.isMuted)) {
      needsBlankRow = true
      target = { ...createBlankRow(), orgItemCode: edit.orgItemCode }
      idx = next.length
      next.push(target)
    }

    if (!target || idx < 0) return

    const editedFields = new Set(target.editedFields)
    if (needsBlankRow) editedFields.add('orgItemCode')
    if (edit.masterRegister) editedFields.add('action:マスタ追加')
    if (edit.isDiscontinued) {
      editedFields.add('isDiscontinued')
      editedFields.add('action:取扱終了')
    }
    if (edit.isMuted) {
      editedFields.add('isMuted')
      editedFields.add('action:通知不要')
    }

    const updated: MstItemWithId = {
      ...target,
      isEdited: true,
      editedFields,
      ...(edit.isDiscontinued ? { isDiscontinued: true } : {}),
      ...(edit.isMuted ? { isMuted: true } : {}),
    }
    next[idx] = updated
    if (needsBlankRow) adds.push(updated)
    else updates.push(updated)
  })

  return { next, adds, updates }
}

function DataList({ 
  list, 
  isLoading, 
  supplier, 
  systype, 
  searchParam, 
  onReload, 
  updateTrigger, 
  onUploadData,
  setHasGridEdited,
  setResultMessage,
  setDownloadColumnDefs,
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  gridRef: externalGridRef,
  isSearchVisible = false,
  dialogEdits = [],
  focusTarget,
  onFocusHandled,
}: DataListProps) {
  const { loginUserInfo } = useAuthContext()
  const {result, fetchUpdateItem, isUpdating} = useItem()
  const internalGridRef = useRef<AgGridReact<MstItemWithId>>(null)
  
  // Use external gridRef if provided, otherwise use internal one
  const gridRef = externalGridRef || internalGridRef
  
  const containerStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${180 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  
  const gridStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${180 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  const [editorDataReady, setEditorDataReady] = useState(false);
  const [row, setRow] = useState<MstItemWithId[]>(
    list.list.map((item) => ({ ...item, ...initRecordEditEx() }))
  )
  const [supplierSelect, setSupplierSelect] = useState<(string | null)[]>([])
  const [itemSelect, setItemSelect] = useState<(string | null)[]>([])
  const [orderUnitTypeSelect, setOrderUnitTypeSelect] = useState<(string | null)[]>([])
  const [orderUnitTypeList, setOrderUnitTypeList] = useState<Codes[]>([])
  const [internalFindSearchValue, setInternalFindSearchValue] = useState<string>()
  
  // Use external value if provided, otherwise use internal state
  const findSearchValue = externalFindSearchValue ?? internalFindSearchValue
  
  useEffect(() => {
    setResultMessage(result)
  }, [result])
  useEffect(() => {
    if (itemSelect.length > 0 && orderUnitTypeSelect.length > 0 && supplierSelect.length > 0) {
      setEditorDataReady(true);
    }
  }, [itemSelect, orderUnitTypeSelect, supplierSelect]);

  useEffect(() => {
    const values = supplier?.map((r) => r.orgSupplierId) || []
    setSupplierSelect(values)
  }, [supplier]);
  useEffect(() => {
    const codelist = systype.find(r => r.typeGroup === 'order_unit_type')?.codes
    if (codelist){
      setOrderUnitTypeList(codelist)
      const values = codelist.map((r) => r.typeCode) || []
      setOrderUnitTypeSelect(values)    
    }
  }, [systype]);  
  useEffect(() => {
    // console.debug("useEffect row", row)
    setEditorDataReady(false)
    const values = row?.map((r) => r.orgItemCode) || []
    setItemSelect([...values])
  }, [row]);

  const createBlankRow = useCallback((): MstItemWithId => ({
      companyId: loginUserInfo?.companyId || null,
      orgItemCode: null,
      itemName: null,
      salesStartdate: null,
      salesEnddate: null,
      longside: null,
      shortside: null,
      height: null,
      weight: 1, // 重量のデフォルト値を1に設定
      itemCode: null,
      janCase: null,
      jan: null,
      gtin13: null,
      gtin14: null,
      itf: null,
      sdp: null,
      replaceOrgItemCode: null,
      makerGln: null,
      orgSupplierId: null,
      supplierCompanyId: null,
      supplierName: null,
      deleteFlg: false,
      caseLot: null,
      minOrderQuantity: null,
      palletQuantityFace: null,
      palletQuantityTier: null,
      maxPalletQuantity: null,
      spec: null,
      orderUnitType: null,
      orderUnitTypeName: null,
      replaceItemCode: null,
      oldItemCode: null,
      makerName: null,
      remarks: null,
      isDiscontinued: false,
      isMuted: false,
      recencyFlg: null,
      isInactive: null,
      unmatchRenewal: null,
      alertTypeName: null,
      alertMessage: null,
      lastArrivalDate: null,
      lastShipDate: null,
      insertDatetime: null,
      updateDatetime: null,
      insertUser: loginUserInfo?.userId || null,
      deleteDatetime: null,
      deleteUser: null,
      updateUser: null,
      isAlert: null,
      ...initRecordEditEx(true)
    }
  ), [])

  // 入力チェック
  const fieldValidate = (pRow: MstItemWithId, field: string | undefined, newValue: any): boolean => {
    let hasErrors = false
    switch (field){
      case "itemName":
      case "orgSupplierId":
        checkAndSetError(pRow, field, validateRequired(newValue));
        break;

      case "orgItemCode":{
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue));
        // 重複チェック
        if (!hasErrors){
          hasErrors = duplicateCheck(pRow, newValue)
        }
        break;
      }

      case "salesStartdate":
      case "salesEnddate":{
        const startDate = field === 'salesStartdate' ? parseDate(newValue) : pRow.salesStartdate || null;
        const endDate = field === 'salesEnddate' ? parseDate(newValue) : pRow.salesEnddate || null;
        // console.log("startDate err", field, startDate, endDate)
        if (startDate && endDate && startDate > endDate) {
          hasErrors = checkAndSetError(pRow, field, "は発売日より後の日付を指定してください。");
        } else {
          checkAndSetError(pRow, "salesStartdate", "");
          checkAndSetError(pRow, "salesEnddate", "");
        }
        break;
      }

      case "weight": {
        hasErrors = checkAndSetError(pRow, field, validateWeight(newValue));
        break;
      }

      case "maxPalletQuantity": {
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue));
        break;
      }

      case "janCase": {
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue)) || 
        checkAndSetError(pRow, field, validate4to14Length(newValue).join(','))
        break;
      }
      case "gtin13":
      case "gtin14":
      case "itf":
      case "sdp":
        hasErrors = checkAndSetError(pRow, field, validate4to14Length(newValue).join(','))
        break;
      case "makerGln":
        hasErrors = checkAndSetError(pRow, field, validate6to10Length(newValue).join(','))
        break;
      case "jan":
        hasErrors = checkAndSetError(pRow, field, validate4to13Length(newValue).join(','))
        break;
      default:
        return false;
    }
    // console.debug("***colDataValidate ", hasErrors, field, newValue)
    return hasErrors

  };

  const cellValueChanged = (event: CellValueChangedEvent<MstItemWithId>, hasError: boolean) => {
    const { data, colDef } = event
    const { field } = colDef

    const updatedRow = { ...data }
    // 値の変更時に、APIのエラーを解消
    switch (field){
      case "replaceOrgItemCode":
      case "orgSupplierId":
      case "orgItemCode":
        fieldValidate(updatedRow, "orgItemCode", updatedRow.orgItemCode);
        break;
      default:
    }
  };

  const alertIconCellRenderer = useCallback((params: any) => {
    if (params.value !== true) return null
    return (
      <span className="action-icon-info has_alert">
        <AnnouncementIcon fontSize="small" />
      </span>
    )
  }, [])

  const updateMstItem = useCallback(
    async (updatedRows: MstItemWithId[]) => {
      const datalist = updatedRows.map(({ isEdited, editedFields, salesStartdate, salesEnddate, errors, ...rest }) => ({
        ...rest,
        salesStartdate: salesStartdate ? convertDateToRequestParam(new Date(salesStartdate)) : null,
        salesEnddate: salesEnddate ? convertDateToRequestParam(new Date(salesEnddate)) : null,
      }));
      
      try {
        await fetchUpdateItem({
          masterItemUpdate: { body: datalist }
        })
        if (onReload) onReload()
      } catch (err: any) {
        if (err?.errors && err.result?.status === "error") {
          const gridApi = gridRef.current?.api
          err.errors.forEach((errrow: any) => {

            const {rowdata}=errrow
            const orgItemCode = (rowdata as { org_item_code: string | null })?.org_item_code;
            const errRow = updatedRows.find((rowdatas) => rowdatas.orgItemCode === orgItemCode); 
            if (errRow) {
              // UpdateAPIのErrorは、社内商品コードをエラー表記して、サーバーから取得したエラーを表示する
              errRow.errors = { orgItemCode: [errrow.errorMessage] }
              const rowNode = gridApi?.getRowNode(errRow.id || '')
              if (rowNode) {
                rowNode.setData(errRow)
              }
            }
          })
        }
      }
    }, [fetchUpdateItem, searchParam]);

  const handleImportExcel = (data: any[]) => {
    // console.log("handleImportExcel",  data)
    const dateFields: (keyof MstItemWithId)[] = ['salesStartdate', 'salesEnddate'];
    const flgFields: (keyof MstItemWithId)[] = ['deleteFlg', 'isMuted', 'isDiscontinued'];
    const intFields: (keyof MstItemWithId)[] = ['caseLot','maxPalletQuantity'];
    const intFieldsZero: (keyof MstItemWithId)[] = ['minOrderQuantity','palletQuantityFace','palletQuantityTier'];
    const numericFields: (keyof MstItemWithId)[] = ['longside','shortside','maxPalletQuantity','weight','height'];
    const typeCodeFields: (keyof MstItemWithId)[] = ['orderUnitType'];
    
    let hasError = false;
    const mappedData = data.map((excelRow: any) => {
      const mappedRow: Partial<MstItemWithId> = {};
      Object.keys(columnMapping).forEach((key) => {
        if (key in excelRow) {
          const tableField = columnMapping[key as keyof typeof columnMapping];
          const importvalue = excelRow[key];
          let setvalue = importvalue || null;
          if (flgFields.includes(tableField as keyof MstItemWithId)) {
            setvalue = parseBoolean(importvalue)
          } else if (typeCodeFields.includes(tableField as keyof MstItemWithId)) {
            const codevalue = typeCodeParser(importvalue, orderUnitTypeList,)
            setvalue = typeCodeParser(codevalue, orderUnitTypeList,)
            if (!codevalue && importvalue) hasError = true;
          } else if (dateFields.includes(tableField as keyof MstItemWithId)) {
            // 日付項目は型変換が必要
            setvalue = parseDate(importvalue) as unknown as undefined;;
          } else if (intFields.includes(tableField as keyof MstItemWithId)) {
            setvalue = parsePositiveInteger(importvalue);
          } else if (intFieldsZero.includes(tableField as keyof MstItemWithId)) {
            setvalue = parsePositiveIntegerZero(importvalue);
          } else if (numericFields.includes(tableField as keyof MstItemWithId)) {
            setvalue = parseNumeric(importvalue);
            // 重量フィールドの場合、値がない場合はデフォルト値1を設定
            if (tableField === 'weight' && !setvalue) {
              setvalue = 1;
            }
          } else {
            setvalue = importvalue ? String(importvalue).trim() : null;;
          }
          // パース出来ないときはスキップ（＝NULL）し、警告メッセージを表示
          if (!isNonInputValue(importvalue) && setvalue === null) hasError = true;
          mappedRow[tableField as keyof MstItemWithId] = setvalue as unknown as undefined
        }
      });
      return mappedRow as MstItemWithId;
    });
    return { mappedData, hasError }
  };


  const {
    booleanFormatter,
    dateFormatter,
    excelStyles,
    selectValueFormatter,
    typecodeFormatter,
    formatBooleanValue,
    formatDecimalValue,
    isNonInputValue,
    numberValueFormatter,
    numberValueParser,
    numberValueParserZero,
    parseBoolean,
    parseBooleanValue,
    parseDate,
    parseDateValue,
    parseDecimalValue,
    parseNumeric,
    parsePositiveInteger,
    parsePositiveIntegerZero,
    parseStringValue,
    selectTypecodeValueParser,
    selectValueParser,
    typeCodeParser,
    localeText,
    autoSizeStrategy,
    onGridReady,
    getCellClassRules,
    checkAndSetError,
    rowDataValidate,
    colDataValidate,
    duplicateCheck,
    isEnableEdit,
    longTextEditorParams,
    longTextValueFormatter,
    longTextValueParser,
    longTextValueSetter,
    editInfoValueGetter,
    dateTreeSetFilterParams,
    clearPinnedRows,
    createCellClassRules,
  } = useGridEdit({
    gridRef,
    isLoading,
    isUpdating,
    onReload,
    onUploadData,
    updateTrigger,
    setHasGridEdited,
    setResultMessage,
    columnMapping,
    primayKey,
    storageKey: 'gridColumnState:/master-item',
    updateData: updateMstItem,
    handleImportExcel,
    createBlankRow,
    fieldValidate,
    cellValueChanged,
    scrollPositionKey: 'orgItemCode',
    rowData: row,
  })  

  const unmatchRenewalCellClassRules = useMemo(
    () => createCellClassRules('unmatchRenewal', true, 'alert_middle'),
    [createCellClassRules]
  )
  const inactiveCellClassRules = useMemo(
    () => createCellClassRules('isInactive', true, 'alert_middle'),
    [createCellClassRules]
  )

  /**
   * カラム定義
   * @returns
   */
  const updateColumnDefs = (): (ColDef | ColGroupDef)[] =>
  [
    {
      headerName: '!',
      rowDrag: true,
      cellRenderer: EditStatusCellRenderer,
      valueGetter: (params) => editInfoValueGetter(params),
      pinned: 'left',
      width: 100,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              values: ['入力エラー', '変更', null],
              refreshValuesOnOpen: true,
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
            },
          },
        ],
        buttons: ['reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: true,
        excelMode: 'mac',
      },
    },
    {
      headerName: 'アラート',
      field: 'isAlert',
      pinned: 'left',
      width: 70,
      cellClass: 'boolean-cell',
      cellRenderer: alertIconCellRenderer,
      tooltipValueGetter: (p) => p.data?.alertTypeName ?? '',
    },
    {
      headerName: '商品設定',
      children: [
        {
          headerName: '社内商品コード',
          field: 'orgItemCode', // 社内商品コード
          editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
          valueParser: parseStringValue,
          pinned: 'left',
          width: 200,
        },
        {
          headerName: '商品名称',
          field: 'itemName', // 商品名称
          editable: isEnableEdit(),
          width: 250,
          valueParser: parseStringValue,
          pinned: 'left',
        },
        {
          headerName: '発売日',
          field: 'salesStartdate', // 発売日
          filter: 'agMultiColumnFilter',
          filterParams: {
            filters: [
              {filter: 'agDateColumnFilter'},
              {filter: 'agSetColumnFilter',
               filterParams: dateTreeSetFilterParams,
              },
            ],
            closeOnApply: true,
          },
          editable: isEnableEdit(),
          width: 150,
          cellEditor: 'agDateCellEditor',
          cellEditorParams: {
            minYear: 1900, 
            maxYear: 2050,
          },
          valueFormatter: dateFormatter,
          valueParser: parseDateValue,
        },
        {
          headerName: '終了日',
          field: 'salesEnddate', // 終了日
          filter: 'agMultiColumnFilter',
          filterParams: {
            filters: [
              {filter: 'agDateColumnFilter'},
              {
                filter: 'agSetColumnFilter',
                filterParams: dateTreeSetFilterParams,
              },
            ],
            closeOnApply: true,
          },
          editable: isEnableEdit(),
          width: 150,
          cellEditor: 'agDateCellEditor',
          cellEditorParams: {
            minYear: 1900, 
            maxYear: 2050,
          },
          valueFormatter: dateFormatter,
          valueParser: parseDateValue,
        },
        {
          headerName: 'ケースサイズ 長手（mm）',
          field: 'longside',
          width: 200,
          editable: isEnableEdit(),
          cellClass: 'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params) => formatDecimalValue(params, 2),
          cellEditor:'agNumberCellEditor',
          valueParser: (params) => parseDecimalValue(params, 2)
        },
        {
          headerName: 'ケースサイズ 短手（mm）',
          field: 'shortside', // ケースサイズ 短手（mm）
          width: 200,
          editable: isEnableEdit(),
          cellClass:'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params) => formatDecimalValue(params, 2),
          cellEditor:'agNumberCellEditor',
          valueParser: (params) => parseDecimalValue(params, 2)
        },
        {
          headerName: 'ケースサイズ 高さ（mm）',
          field: 'height', // ケースサイズ 高さ（mm）
          width: 200,
          editable: isEnableEdit(),
          cellClass:'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params) => formatDecimalValue(params, 2),
          cellEditor:'agNumberCellEditor',
          valueParser: (params) => parseDecimalValue(params, 2)
        },
        {
          headerName: 'ケースサイズ 重量（g）',
          field: 'weight', // ケースサイズ 重量（g）
          width: 200,
          editable: isEnableEdit(),
          cellClass:'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: (params) => formatDecimalValue(params, 2),
          cellEditor:'agNumberCellEditor',
          valueParser: (params) => parseDecimalValue(params, 2)
        },
        {
          headerName: '規格',
          field: 'spec', // 規格
          width: 200,
          editable: isEnableEdit(),
          valueParser: parseStringValue,
        },
        {
          headerName: 'パレット最大積付数',
          field: 'maxPalletQuantity', // パレット最大積付数
          width: 200,
          editable: isEnableEdit(),
          cellClass:'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: numberValueFormatter,
          cellEditor:'agNumberCellEditor',
          valueParser: numberValueParser,
        },
        {
          headerName: 'パレット積載数（面）',
          field: 'palletQuantityFace', // パレット積載数（面）
          width: 200,
          editable: isEnableEdit(),
          cellClass:'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: numberValueFormatter,
          cellEditor:'agNumberCellEditor',
          valueParser: numberValueParserZero,
        },
        {
          headerName: 'パレット積載数（段）',
          field: 'palletQuantityTier', // パレット積載数（段）
          width: 200,
          editable: isEnableEdit(),
          cellClass:'number-cell',
          filter: 'agNumberColumnFilter',
          valueFormatter: numberValueFormatter,
          cellEditor:'agNumberCellEditor',
          valueParser: numberValueParserZero,
        },
        {
          headerName: 'JANコード（バラ）',
          field: 'jan', // JANコード（バラ）
          width: 200,
          editable: isEnableEdit(),
          valueParser: parseStringValue,
        },
        {
          headerName: 'JANコード（ケース）',
          field: 'janCase', // JANコード（ケース）
          width: 200,
          editable: isEnableEdit(),
          valueParser: parseStringValue,
        },
        {
          headerName: 'GTINコード（バラ）',
          field: 'gtin13', // GTINコード（バラ）
          width: 200,
          editable: isEnableEdit(),
          valueParser: parseStringValue,
        },
        {
          headerName: 'GTINコード（ケース）',
          field: 'gtin14', // GTINコード（ケース）
          width: 200,
          editable: isEnableEdit(),
          valueParser: parseStringValue,
        },
        {
          headerName: 'ITFコード',
          field: 'itf', // ITFコード
          width: 200,
          editable: isEnableEdit(),
          valueParser: parseStringValue,
        },
        {
          headerName: 'SDPコード',
          field: 'sdp', // SDPコード
          width: 200,
          editable: isEnableEdit(),
          valueParser: parseStringValue,
        },
        {
          headerName: 'メーカーGS1事業者コード',
          field: 'makerGln', // メーカーGS1事業者コード
          width: 200,
          editable: isEnableEdit(),
          valueParser: parseStringValue,
        },
        {
          headerName: 'メーカー名',
          field: 'makerName', // メーカー名
          editable: isEnableEdit(),
          width: 200,
          valueParser: parseStringValue,
        },
        {
          headerName: '仕入先ID',
          field: 'orgSupplierId', // 仕入先ID
          editable: isEnableEdit(),
          width: 220,
          cellEditor: 'agRichSelectCellEditor',
          valueFormatter: (params: ValueFormatterParams) => selectValueFormatter(params.value, supplier, "orgSupplierId", "supplierName"),
          filterValueGetter: (params) => selectValueFormatter(params.data.orgSupplierId, supplier, "orgSupplierId", "supplierName"),
          valueParser: (params: ValueParserParams) => selectValueParser(params, supplier, "orgSupplierId"),
          cellEditorParams: editorDataReady ? {
            values : [null, ...supplierSelect],
            formatValue: (value: string) => selectValueFormatter(value, supplier, "orgSupplierId", "supplierName"),
            searchType: "match",
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
          } as IRichCellEditorParams : {values: ['-']},
        },
      ],
    },
    {
      headerName: '発注設定',
      children: [
        {
          headerName: '後継社内商品コード',
          field: 'replaceOrgItemCode', // 後継社内商品コード
          editable: isEnableEdit(),
          width: 300,
          cellClassRules: unmatchRenewalCellClassRules,
          cellEditor: 'agRichSelectCellEditor',
          valueFormatter: (params: ValueFormatterParams) => selectValueFormatter(params.value, row, "orgItemCode", "itemName"),
          filterValueGetter: (params) => selectValueFormatter(params.data.orgItemCode, row, "orgItemCode", "itemName"),
          valueParser: (params: ValueParserParams) => selectValueParser(params, row, "orgItemCode"),
          cellEditorParams: editorDataReady ? {
            values : [null, ...itemSelect],
            formatValue: (value: string) => selectValueFormatter(value, row, "orgItemCode", "itemName"),
            searchType: "matchAny",
            allowTyping: true,
            filterList: true,
            highlightMatch: true,
          } as IRichCellEditorParams : {values: ['-']},
        },
        {
          headerName: '発注単位',
          field: 'orderUnitType', // 発注単位
          width: 200,
          suppressHeaderMenuButton: false,
          editable: isEnableEdit(),
          cellEditor: 'agRichSelectCellEditor',
          // 区分値は、ドロップダウン後のリストはコード＋名称で、表記は名称のみ
          valueFormatter: (params: ValueFormatterParams) => typecodeFormatter(params.value, orderUnitTypeList),
          filterValueGetter: (params) => typecodeFormatter(params.data.orderUnitType, orderUnitTypeList),
          valueParser: (params: ValueParserParams) => selectTypecodeValueParser(params, orderUnitTypeList),
          cellEditorParams: editorDataReady ? {
            values : [null, ...orderUnitTypeSelect],
            formatValue: (value: string) => selectValueFormatter(value, orderUnitTypeList, "typeCode", "typeName")
          } as IRichCellEditorParams : {values: ['-']},
        },
        {
          headerName: 'ケースロット数',
          field: 'caseLot', // ケースロット数
          editable: isEnableEdit(),
          width: 200,
          valueFormatter: numberValueFormatter,
          cellClass:'number-cell',
          filter: 'agNumberColumnFilter',
          cellEditor:'agNumberCellEditor',
          valueParser: numberValueParser,
        },
        {
          headerName: '最低発注量',
          field: 'minOrderQuantity', // 最低発注量
          editable: isEnableEdit(),
          width: 200,
          valueFormatter: numberValueFormatter,
          cellClass:'number-cell',
          filter: 'agNumberColumnFilter',
          cellEditor:'agNumberCellEditor',
          valueParser: numberValueParserZero,
        },
      ],
    },
    {
      headerName: '管理項目',
      children: [
        {
          headerName: '通知停止',
          field: 'isMuted', // 通知停止
          editable: isEnableEdit(),
          width: 200,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue, 
        },
        {
          headerName: '取扱終了',
          field: 'isDiscontinued', // 取扱終了
          editable: isEnableEdit(),
          width: 200,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue, 
        },
        {
          headerName: '削除',
          field: 'deleteFlg', // 削除
          editable: isEnableEdit(),
          width: 200,
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          valueFormatter: formatBooleanValue,
          valueParser: parseBooleanValue, 
        },
        {
          headerName: '備考',
          field: 'remarks', 
          editable: isEnableEdit(),
          width: 200,
          cellEditorPopup: true,
          cellEditor: 'agLargeTextCellEditor',
          cellEditorParams: longTextEditorParams,
          valueFormatter: longTextValueFormatter,
          valueParser: longTextValueParser,
          valueSetter: longTextValueSetter,
        },
      ],
    },
    {
      headerName: 'PSI計算項目',
      children: [
        {
          headerName: '最終入荷日',
          field: 'lastArrivalDate', // 最終入荷日
          width: 200,
          cellClassRules: inactiveCellClassRules,
          valueParser: parseDateValue,
          valueFormatter: dateFormatter,
          filter: 'agDateColumnFilter',
        },
        {
          headerName: '最終出荷日',
          field: 'lastShipDate', // 最終出荷日
          width: 200,
          cellClassRules: inactiveCellClassRules,
          valueParser: parseDateValue,
          valueFormatter: dateFormatter,
          filter: 'agDateColumnFilter',
        },
        {
          headerName: '最新在庫数',
          field: 'lastStockQuantity', // 最新在庫数
          width: 200,
          valueFormatter: numberValueFormatter,
          cellClass:'number-cell',
          cellClassRules: inactiveCellClassRules,
          filter: 'agNumberColumnFilter',
        },
      ],
    },

  ];

  const onFilterChanged = useCallback(() => {
    const gridApi = gridRef.current?.api;
  
    if (gridApi) {
      const allColumns = gridApi.getColumns();
      if (allColumns) {
        allColumns.forEach((column) => {
          gridApi.getColumnFilterInstance(column.getColId())
            .then((filterInstance) => {
              if (filterInstance && 'refreshFilterValues' in filterInstance) {
                (filterInstance as any).refreshFilterValues();
              }
            });
        });
      }
      gridApi.redrawRows();
    }
  }, []);

  const columnDefs = useMemo<(ColDef | ColGroupDef)[]>(updateColumnDefs, [editorDataReady])

  // columnDefsが再計算された際に、保存済みのカラム表示設定を再適用する
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return
    try {
      const savedAll = localStorage.getItem('gridColumnStates')
      if (savedAll) {
        const obj = JSON.parse(savedAll) as Record<string, any>
        const state = obj?.['gridColumnState:/master-item']
        if (Array.isArray(state) && state.length > 0) {
          // requestAnimationFrameでAG GridがcolumnDefsを処理した後に適用
          requestAnimationFrame(() => {
            if (!api.isDestroyed()) {
              api.applyColumnState({ state, applyOrder: true })
            }
          })
        }
      }
    } catch { /* noop */ }
  }, [columnDefs])

  // ダウンロードファイル用のカラム定義
  useEffect(() => {
    if (setDownloadColumnDefs)
      setDownloadColumnDefs([
        { headerName: 'PSI企業ID', children: [{ headerName: 'company_id', field: 'companyId' }] },
        { headerName: 'アイテムコード', children: [{ headerName: 'item_code', field: 'itemCode', cellClass: 'stringType' }] },
        { headerName: '社内商品コード', children: [{ headerName: 'org_item_code', field: 'orgItemCode', cellClass: 'stringType' }] },
        { headerName: '商品名称', children: [{ headerName: 'item_name', field: 'itemName', cellClass: 'stringType' }] },
        { headerName: '発売日', children: [{ headerName: 'sales_startdate', field: 'salesStartdate', valueFormatter: dateFormatter}] },
        { headerName: '終了日', children: [{ headerName: 'sales_enddate', field: 'salesEnddate', valueFormatter: dateFormatter }] },
        { headerName: 'ケースサイズ 長手（mm）', children: [{ headerName: 'longside', field: 'longside', cellClass: 'decimalType' }] },
        { headerName: 'ケースサイズ 短手（mm）', children: [{ headerName: 'shortside', field: 'shortside', cellClass: 'decimalType' }] },
        { headerName: 'ケースサイズ 高さ（mm）', children: [{ headerName: 'height', field: 'height', cellClass: 'decimalType' }] },
        { headerName: 'ケースサイズ 重量（g）', children: [{ headerName: 'weight', field: 'weight', cellClass: 'decimalType' }] },
        { headerName: '規格', children: [{ headerName: 'spec', field: 'spec', cellClass: 'stringType' }] },
        { headerName: 'パレット最大積付数', children: [{ headerName: 'max_pallet_quantity', field: 'maxPalletQuantity', cellClass: 'numberType' }] },
        { headerName: 'パレット積載数（面）', children: [{ headerName: 'pallet_quantity_face', field: 'palletQuantityFace', cellClass: 'numberType' }] },
        { headerName: 'パレット積載数（段）', children: [{ headerName: 'pallet_quantity_tier', field: 'palletQuantityTier', cellClass: 'numberType' }] },
        { headerName: 'JANコード（バラ）', children: [{ headerName: 'jan', field: 'jan', cellClass: 'stringType' }] },
        { headerName: 'JANコード（ケース）', children: [{ headerName: 'jan_case', field: 'janCase', cellClass: 'stringType' }] },
        { headerName: 'GTINコード（バラ）', children: [{ headerName: 'gtin_13', field: 'gtin13', cellClass: 'stringType' }] },
        { headerName: 'GTINコード（ケース）', children: [{ headerName: 'gtin_14', field: 'gtin14', cellClass: 'stringType' }] },
        { headerName: 'ITFコード', children: [{ headerName: 'itf', field: 'itf', cellClass: 'stringType' }] },
        { headerName: 'SDPコード', children: [{ headerName: 'sdp', field: 'sdp', cellClass: 'stringType' }] },
        { headerName: 'メーカーGS1事業者コード', children: [{ headerName: 'maker_gln', field: 'makerGln', cellClass: 'stringType' }] },
        { headerName: 'メーカー名', children: [{ headerName: 'maker_name', field: 'makerName', cellClass: 'stringType' }] },
        { headerName: '仕入先ID', children: [{ headerName: 'org_supplier_id', field: 'orgSupplierId', cellClass: 'stringType' }] },
        { headerName: '仕入先名', children: [{ headerName: 'supplier_name', field: 'supplierName', cellClass: 'stringType' }] },
        { headerName: '後継社内商品コード', children: [{ headerName: 'replace_org_item_code', field: 'replaceOrgItemCode', cellClass: 'stringType' }] },
        { headerName: '発注単位', children: [{ headerName: 'order_unit_type', field: 'orderUnitType', cellClass: 'stringType' }] },
        { headerName: 'ケースロット数', children: [{ headerName: 'case_lot', field: 'caseLot', cellClass: 'numberType' }] },
        { headerName: '最低発注量', children: [{ headerName: 'min_order_quantity', field: 'minOrderQuantity', cellClass: 'numberType' }] },
        { headerName: '通知停止', children: [{ headerName: 'is_muted', field: 'isMuted', cellClass: 'booleanType' }] },
        { headerName: '取扱終了', children: [{ headerName: 'is_discontinued', field: 'isDiscontinued', cellClass: 'booleanType' }] },
        { headerName: '備考', children: [{ headerName: 'remarks', field: 'remarks', cellClass: 'stringType' }] },
        { headerName: '削除', children: [{ headerName: 'delete_flg', field: 'deleteFlg', cellClass: 'booleanType' }] },
        { headerName: '最終入荷日', children: [{ headerName: 'last_arrival_date', field: 'lastArrivalDate', valueFormatter: dateFormatter }] },
        { headerName: '最終出荷日', children: [{ headerName: 'last_ship_date', field: 'lastShipDate', valueFormatter: dateFormatter }] },
        { headerName: '最新在庫数', children: [{ headerName: 'last_stock_quantity', field: 'lastStockQuantity', cellClass: 'numberType' }] },
      ])
  }, [setDownloadColumnDefs])

  useEffect(() => {
    if (list.list.length === 0) {
      const blankRow = createBlankRow();
      setRow([blankRow]);
    } else {
      setRow(list.list.map(item => ({ ...item, ...initRecordEditEx()})))
      clearPinnedRows()
    }
  }, [list.list]);

  useEffect(() => {
    if (!dialogEdits?.length) return

    let adds: MstItemWithId[] = []
    let updates: MstItemWithId[] = []

    setRow((prev) => {
      const dialogApplyResult = applyItemDialogEdits(prev, dialogEdits, createBlankRow)
      adds = dialogApplyResult.adds
      updates = dialogApplyResult.updates
      return dialogApplyResult.next
    })

    if (adds.length === 0 && updates.length === 0) return

    setHasGridEdited(true)
    syncDialogEditsToAgGrid(gridRef, adds, updates)
  }, [createBlankRow, dialogEdits, gridRef, setHasGridEdited])

  useEffect(() => {
    if (!focusTarget?.orgItemCode) return
    const gridApi = gridRef.current?.api
    if (!gridApi) return
    const matchCode = String(focusTarget.orgItemCode).trim()
    const run = () => {
      gridApi.deselectAll()
      gridApi.forEachNode((node) => {
        const code = String(node.data?.orgItemCode ?? '').trim()
        if (code !== matchCode || node.rowIndex == null) return
        gridApi.ensureIndexVisible(node.rowIndex, 'middle')
        node.setSelected(true, true)
      })
      onFocusHandled?.()
    }
    requestAnimationFrame(() => requestAnimationFrame(run))
  }, [focusTarget, gridRef, onFocusHandled])

  return (
    <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            rowData={row}
            ref={gridRef}
            getRowId={(params) => params.data.id}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            localeText={localeText}
            autoSizeStrategy={autoSizeStrategy}
            loadingOverlayComponent={LoadingOverlay}
            findSearchValue={findSearchValue}
            onFindChanged={externalOnFindChanged}
            stopEditingWhenCellsLoseFocus
          />
        </div>
      </div>
    </div>
  );
}

export default DataList;
